import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, Truck } from 'lucide-react';
import { ImageUpload } from '../ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function FleetManager() {
  const [vehicles, setVehicles] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'OUR FLEET', hero_subtitle_bg: 'НАШИЯТ ФЛОТ', hero_subtitle_de: 'UNSER FUHRPARK',
    hero_title_en: 'Modern', hero_title_bg: 'Модерен', hero_title_de: 'Moderner',
    hero_title_highlight_en: 'EURO 6', hero_title_highlight_bg: 'EURO 6', hero_title_highlight_de: 'EURO 6',
    hero_title_end_en: 'Fleet', hero_title_end_bg: 'флот', hero_title_end_de: 'Fuhrpark',
    hero_description_en: 'Our diverse fleet covers cargo volumes from 11 m³ to 121 m³.',
    hero_description_bg: 'Нашият разнообразен флот покрива обеми на товари от 11 m³ до 121 m³.',
    hero_description_de: 'Unser vielfältiger Fuhrpark deckt Ladevolumen von 11 m³ bis 121 m³ ab.',
    features_en: ['EURO 6 emission standards', 'ECO zone access certification', 'GPS tracking on every vehicle'],
    features_bg: ['EURO 6 емисионни стандарти', 'ECO зона достъп сертификат', 'GPS проследяване на всяко превозно средство'],
    features_de: ['EURO 6 Emissionsstandards', 'Zugang zu Umweltzonen', 'GPS-Verfolgung an jedem Fahrzeug'],
    cta_title_en: 'Not Sure Which Vehicle You Need?', 
    cta_title_bg: 'Не сте сигурни кое превозно средство ви трябва?',
    cta_title_de: 'Nicht sicher, welches Fahrzeug Sie brauchen?',
    cta_button_en: 'Get Expert Advice', cta_button_bg: 'Получете експертен съвет', cta_button_de: 'Expertenberatung erhalten'
  });
  
  const defaultForm = {
    name: '',
    type_en: '', type_bg: '', type_de: '',
    capacity_m3: '', payload_kg: '', euro_pallets: '', dimensions: '',
    eco_standard: 'EURO 6', image_url: '', order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchVehicles(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/fleet-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/fleet-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/admin/fleet`);
      setVehicles(response.data.fleet || response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await axios.put(`${API}/admin/fleet/${editingVehicle.id}`, formData);
        toast.success('Vehicle updated!');
      } else {
        await axios.post(`${API}/admin/fleet`, formData);
        toast.success('Vehicle added!');
      }
      fetchVehicles();
      setIsFormOpen(false);
      setEditingVehicle(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving vehicle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await axios.delete(`${API}/admin/fleet/${id}`);
      toast.success('Vehicle deleted!');
      fetchVehicles();
    } catch (error) {
      toast.error('Error deleting vehicle');
    }
  };

  const startEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name || '',
      type_en: vehicle.type_en || vehicle.type || '',
      type_bg: vehicle.type_bg || '',
      type_de: vehicle.type_de || '',
      capacity_m3: vehicle.capacity_m3 || '',
      payload_kg: vehicle.payload_kg || '',
      euro_pallets: vehicle.euro_pallets || '',
      dimensions: vehicle.dimensions || '',
      eco_standard: vehicle.eco_standard || 'EURO 6',
      image_url: vehicle.image_url || '',
      order: vehicle.order || 0,
      is_active: vehicle.is_active !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePageField = (field, value) => {
    setPageContent(prev => ({ ...prev, [field]: value }));
  };

  const updateFeature = (lang, index, value) => {
    const field = `features_${lang}`;
    const features = [...(pageContent[field] || [])];
    features[index] = value;
    setPageContent(prev => ({ ...prev, [field]: features }));
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Fleet Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div className="grid gap-3">
                        <div>
                          <Label className="text-xs">Subtitle</Label>
                          <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Title Start</Label>
                            <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                          <div>
                            <Label className="text-xs">Title Highlight</Label>
                            <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                          <div>
                            <Label className="text-xs">Title End</Label>
                            <Input value={pageContent[`hero_title_end_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_end_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={2} />
                        </div>
                        <div>
                          <Label className="text-xs">Features (checkmarks)</Label>
                          {(pageContent[`features_${lang.code}`] || []).map((feat, idx) => (
                            <Input key={idx} value={feat} onChange={(e) => updateFeature(lang.code, idx, e.target.value)} className="mt-1 bg-white" placeholder={`Feature ${idx + 1}`} />
                          ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">CTA Title</Label>
                            <Input value={pageContent[`cta_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                          <div>
                            <Label className="text-xs">CTA Button</Label>
                            <Input value={pageContent[`cta_button_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_button_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Fleet Vehicles</h2>
          <p className="text-sm text-slate-500">Manage your vehicle fleet</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingVehicle(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Vehicle
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingVehicle ? 'Edit Vehicle' : 'New Vehicle'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setIsFormOpen(false); setEditingVehicle(null); }}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Vehicle Name */}
              <div>
                <Label>Vehicle Name *</Label>
                <Input value={formData.name} onChange={(e) => updateField('name', e.target.value)} className="mt-1 rounded-xl" required placeholder="e.g., Mercedes Sprinter" />
              </div>

              {/* Language Tabs for Type */}
              <div>
                <Label className="mb-2 block">Vehicle Type (Translations)</Label>
                <Tabs value={activeLang} onValueChange={setActiveLang} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                        {lang.flag} {lang.name}
                        {formData[`type_${lang.code}`] && <span className="ml-1 text-green-500">✓</span>}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code}>
                      <div className={`p-4 rounded-2xl ${lang.bg}`}>
                        <Input 
                          value={formData[`type_${lang.code}`]} 
                          onChange={(e) => updateField(`type_${lang.code}`, e.target.value)} 
                          className="rounded-xl bg-white" 
                          required={lang.code === 'en'}
                          placeholder={lang.code === 'en' ? 'e.g., Van, Truck' : lang.code === 'bg' ? 'напр., Ван, Камион' : 'z.B., Transporter, LKW'}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Capacity (m³)</Label>
                  <Input value={formData.capacity_m3} onChange={(e) => updateField('capacity_m3', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g., 20" />
                </div>
                <div>
                  <Label>Payload (kg)</Label>
                  <Input value={formData.payload_kg} onChange={(e) => updateField('payload_kg', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g., 1500" />
                </div>
                <div>
                  <Label>Euro Pallets</Label>
                  <Input value={formData.euro_pallets} onChange={(e) => updateField('euro_pallets', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g., 8" />
                </div>
                <div>
                  <Label>Dimensions</Label>
                  <Input value={formData.dimensions} onChange={(e) => updateField('dimensions', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g., 4.3x1.8x2.0" />
                </div>
              </div>

              {/* Image & Order */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Image</Label>
                  <div className="mt-1">
                    <ImageUpload value={formData.image_url} onChange={(url) => updateField('image_url', url)} placeholder="Enter URL or upload" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Order</Label>
                    <Input type="number" value={formData.order} onChange={(e) => updateField('order', parseInt(e.target.value) || 0)} className="mt-1 rounded-xl" />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} className="rounded" />
                      <span className="text-sm">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsFormOpen(false); setEditingVehicle(null); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full"><Save className="w-4 h-4 mr-2" /> Save Vehicle</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Vehicles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map((vehicle) => (
          <Card key={vehicle.id} className={`bg-white rounded-2xl border-0 shadow-sm ${!vehicle.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {vehicle.image_url ? (
                  <img src={vehicle.image_url} alt={vehicle.name} className="w-16 h-16 object-cover rounded-xl" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-8 h-8 text-[#C41E3A]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-slate-900 truncate">{vehicle.name}</h4>
                  <p className="text-sm text-slate-500">{vehicle.type_en || vehicle.type}</p>
                  {/* Language badges */}
                  <div className="flex gap-1 mt-1">
                    {LANGUAGES.map(lang => (
                      <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${vehicle[`type_${lang.code}`] ? lang.bg + ' ' + lang.text : 'bg-slate-100 text-slate-400'}`}>
                        {lang.flag}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{vehicle.capacity_m3}m³ • {vehicle.payload_kg}kg • {vehicle.euro_pallets} pallets</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => startEdit(vehicle)} className="rounded-full flex-1"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(vehicle.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {vehicles.length === 0 && <p className="text-slate-500 text-center py-8 col-span-3">No vehicles yet. Add your first vehicle!</p>}
      </div>
    </div>
  );
}
